import { useCallback } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { PDPFormValues, ActionStep } from 'types/pdp';
import { RelatedArea } from 'constants/pdp';

export default function useDevelopmentNeedsField(
  form: UseFormReturn<PDPFormValues>,
) {
  const { getValues, control } = form;

  const {
    fields: developmentNeeds,
    append: appendNeed,
    remove: removeNeed,
    update: updateNeed,
  } = useFieldArray<PDPFormValues, 'developmentNeeds', 'key'>({
    control,
    name: 'developmentNeeds',
    keyName: 'key',
  });

  const addDevelopmentNeed = useCallback(() => {
    appendNeed({
      name: '',
      relatedArea: RelatedArea.EXPERTISE,
      deadline: '',
      actionSteps: [],
    });
  }, [appendNeed]);

  const updateDevelopmentNeedField = useCallback(
    (
      index: number,
      patch: Partial<PDPFormValues['developmentNeeds'][number]>,
    ) => {
      const current = developmentNeeds[index] as any;
      updateNeed(index, { ...current, ...patch });
    },
    [developmentNeeds, updateNeed],
  );

  const addActionStep = useCallback(
    (needIndex: number) => {
      const currentNeeds = getValues('developmentNeeds') || [];
      const need = currentNeeds[needIndex];
      const nextSteps = [
        ...(need?.actionSteps || []),
        { name: '', isAchieved: false } as ActionStep,
      ];
      updateNeed(needIndex, { ...need, actionSteps: nextSteps });
    },
    [getValues, updateNeed],
  );

  const updateActionStep = useCallback(
    (needIndex: number, stepIndex: number, patch: Partial<ActionStep>) => {
      const currentNeeds = getValues('developmentNeeds') || [];
      const need = currentNeeds[needIndex];
      const steps = [...(need?.actionSteps || [])];
      steps[stepIndex] = { ...steps[stepIndex], ...patch } as ActionStep;
      updateNeed(needIndex, { ...need, actionSteps: steps });
    },
    [getValues, updateNeed],
  );

  const removeActionStep = useCallback(
    (needIndex: number, stepIndex: number) => {
      const currentNeeds = getValues('developmentNeeds') || [];
      const need = currentNeeds[needIndex];
      const steps = [...(need?.actionSteps || [])];
      steps.splice(stepIndex, 1);
      updateNeed(needIndex, { ...need, actionSteps: steps });
    },
    [getValues, updateNeed],
  );

  return {
    developmentNeeds,
    addDevelopmentNeed,
    removeDevelopmentNeed: removeNeed,
    updateDevelopmentNeedField,
    addActionStep,
    updateActionStep,
    removeActionStep,
  };
}
